import { useEffect, useMemo, useState } from 'react'
import { Button, Card, Typography, Alert, Form, Input, Segmented, Divider, theme } from 'antd'
import { LoginOutlined, MailOutlined, LockOutlined, SunOutlined, MoonOutlined, DesktopOutlined } from '@ant-design/icons'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { authAPI } from '@/api/auth'
import { useAuthStore } from '@/store/auth'
import { AxiosError } from 'axios'
import { useThemeStore } from '@/store/theme'
import { useCapabilitiesStore } from '@/store/capabilities'
import { useImpersonationStore } from '@/store/impersonation'
import { getHostBranding } from '@/utils/hostBranding'
import { writeDomainCookie } from '@/utils/uiPrefs'

const { Title, Text } = Typography
const EXTERNAL_IDP_COOKIE = 'medcifra-force-idp'
const EXTERNAL_IDP_BRIDGE_COOKIE = 'medcifra-auth-bridge'
const EXTERNAL_IDP_MAX_AGE_SECONDS = 120
const EXTERNAL_IDP_BRIDGE_MAX_AGE_SECONDS = 45

const ERROR_MESSAGES: Record<string, string> = {
  auth_failed: 'Ошибка аутентификации. Попробуйте ещё раз.',
  userinfo_failed: 'Не удалось получить данные пользователя.',
  callback_failed: 'Ошибка при обработке входа.',
  invalid_credentials: 'Неверный email или пароль.',
  admin_access_required: 'У вашей учетной записи нет доступа к административному порталу.',
  yandex_auth_failed: 'Ошибка входа через Яндекс. Попробуйте ещё раз.',
  yandex_userinfo_failed: 'Не удалось получить данные пользователя после входа через Яндекс.',
  yandex_account_not_linked: 'Этот Яндекс-аккаунт не привязан к учетной записи Медцифры.',
}

const PASSWORD_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_PASSWORD_LOGIN === 'true'
const MEDCIFRA_ID_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_MEDCIFRA_ID_LOGIN !== 'false'
const DIRECT_YANDEX_LOGIN_ENABLED = import.meta.env.VITE_ENABLE_DIRECT_YANDEX_LOGIN === 'true'

const TEXTS = {
  loginErrorDefault: 'Ошибка аутентификации. Попробуйте ещё раз.',
  platform: 'Платформа медицинской аналитики',
  oidcDescription: 'Основной путь входа идет через id.medcifra.ru.',
  viaMedcifra: 'Через Медцифра ID',
  viaYandex: 'Через Яндекс ID',
  emailPlaceholder: 'Email',
  passwordPlaceholder: 'Пароль',
  login: 'Войти',
  continueMedcifra: 'Войти через Медцифра ID',
  continueYandex: 'Войти через Яндекс ID',
  passwordSectionTitle: 'Технический вход по email и паролю',
  passwordSectionHint: 'Использует Zitadel password grant. Держать включенным только для controlled smoke/debug.',
}

function LoginPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const error = searchParams.get('error')
  const { token: t } = theme.useToken()
  const [loading, setLoading] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)
  const [showPasswordForm, setShowPasswordForm] = useState(false)
  const queryClient = useQueryClient()
  const setTokens = useAuthStore((state) => state.setTokens)
  const fetchUser = useAuthStore((state) => state.fetchUser)
  const themeMode = useThemeStore((state) => state.mode)
  const setThemeMode = useThemeStore((state) => state.setMode)
  const brand = useMemo(() => getHostBranding(window.location.hostname), [])
  const texts = TEXTS

  useEffect(() => {
    localStorage.removeItem('medcifra-auth')
  }, [])

  useEffect(() => {
    const link = document.querySelector("link[rel~='icon']") || document.createElement('link')
    link.setAttribute('rel', 'icon')
    link.setAttribute('href', brand.faviconHref)
    document.head.appendChild(link)
  }, [brand.faviconHref])

  const handleOidcLogin = () => {
    setFormError(null)
    window.location.href = '/api/v1/auth/oidc/login?prompt=login'
  }

  const handleYandexLogin = () => {
    setFormError(null)
    if (DIRECT_YANDEX_LOGIN_ENABLED) {
      window.location.href = '/api/v1/auth/yandex/login'
      return
    }
    writeDomainCookie(EXTERNAL_IDP_COOKIE, 'yandex', EXTERNAL_IDP_MAX_AGE_SECONDS)
    writeDomainCookie(EXTERNAL_IDP_BRIDGE_COOKIE, 'yandex', EXTERNAL_IDP_BRIDGE_MAX_AGE_SECONDS)
    window.location.href = '/api/v1/auth/oidc/login?prompt=login'
  }

  const handlePasswordLogin = async (values: { email: string; password: string }) => {
    setLoading(true)
    setFormError(null)
    try {
      queryClient.clear()
      useCapabilitiesStore.getState().resetCapabilities()
      useImpersonationStore.setState({ impersonatedOrgId: null, impersonatedOrgName: null })
      const response = await authAPI.passwordLogin(values.email, values.password)
      setTokens(response.access_token, response.refresh_token)
      await fetchUser()
      navigate('/', { replace: true })
    } catch (err) {
      const axiosError = err as AxiosError<{ detail?: string }>
      const detail = axiosError.response?.data?.detail
      setFormError(ERROR_MESSAGES[detail ?? ''] || texts.loginErrorDefault)
    } finally {
      setLoading(false)
    }
  }

  const displayError = formError || (error ? (ERROR_MESSAGES[error] || 'Произошла ошибка') : null)
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: t.colorBgLayout,
        gap: 16,
        padding: 16,
      }}
    >
      <Card style={{ width: 400, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <Segmented
            size="small"
            value={themeMode}
            onChange={(value) => setThemeMode(value as 'light' | 'dark' | 'system')}
            options={[
              { value: 'light', icon: <SunOutlined /> },
              { value: 'dark', icon: <MoonOutlined /> },
              { value: 'system', icon: <DesktopOutlined /> },
            ]}
          />
        </div>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img
            src={brand.markSrc}
            alt={brand.productName}
            style={{ height: 64, marginBottom: 16 }}
          />
          <Title level={2} style={{ marginBottom: 8 }}>{brand.productName}</Title>
          <Text type="secondary">{brand.subtitle || texts.platform}</Text>
        </div>

        {displayError && (
          <Alert
            type="error"
            message={displayError}
            showIcon
            style={{ marginBottom: 24 }}
          />
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {MEDCIFRA_ID_LOGIN_ENABLED ? (
            <Button
              type="primary"
              size="large"
              block
              icon={<LoginOutlined />}
              loading={loading}
              onClick={handleOidcLogin}
            >
              {texts.continueMedcifra}
            </Button>
          ) : null}
          <Button
            size="large"
            block
            icon={<LoginOutlined />}
            loading={loading}
            onClick={handleYandexLogin}
          >
            {texts.continueYandex}
          </Button>
        </div>

        <Text
          type="secondary"
          style={{ display: 'block', marginTop: 16, textAlign: 'center' }}
        >
          {texts.oidcDescription}
        </Text>

        {PASSWORD_LOGIN_ENABLED && (
          <>
            <Divider plain>{texts.passwordSectionTitle}</Divider>
            <Alert
              type="warning"
              showIcon
              message={texts.passwordSectionHint}
              style={{ marginBottom: 16 }}
            />
            {!showPasswordForm ? (
              <Button
                size="large"
                block
                onClick={() => {
                  setFormError(null)
                  setShowPasswordForm(true)
                }}
              >
                {texts.passwordSectionTitle}
              </Button>
            ) : (
              <Form
                layout="vertical"
                onFinish={handlePasswordLogin}
                autoComplete="on"
              >
                <Form.Item
                  name="email"
                  rules={[
                    { required: true, message: 'Введите email' },
                    { type: 'email', message: 'Некорректный email' },
                  ]}
                >
                  <Input
                    prefix={<MailOutlined />}
                    placeholder={texts.emailPlaceholder}
                    size="large"
                    autoComplete="email"
                  />
                </Form.Item>
                <Form.Item
                  name="password"
                  rules={[{ required: true, message: 'Введите пароль' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder={texts.passwordPlaceholder}
                    size="large"
                    autoComplete="current-password"
                  />
                </Form.Item>
                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    size="large"
                    block
                    loading={loading}
                    icon={<LoginOutlined />}
                  >
                    {texts.login}
                  </Button>
                </Form.Item>
              </Form>
            )}
          </>
        )}
      </Card>
    </div>
  )
}

export default LoginPage
